<template>
  <DashboardContainer :pageTitle="pageTitle">
    <GivarooSpinner v-if="loading" class="mt-24" />

    <template v-else>
      <!-- Journey hero + overall progress -->
      <section class="journey-hero">
        <div class="journey-hero__text">
          <h2 class="journey-hero__title">Your Journey</h2>
          <p class="journey-hero__sub">
            {{ unlockedCount }} of {{ totalCount }} badges unlocked · {{ totalPoints }} points
          </p>
        </div>
        <div class="journey-hero__progress">
          <div class="journey-hero__bar">
            <div class="journey-hero__fill" :style="{ width: progressPct + '%' }"></div>
          </div>
          <span class="journey-hero__pct">{{ progressPct }}%</span>
        </div>
      </section>

      <div class="tabs" role="tablist" aria-label="Achievement views">
        <button
          type="button"
          role="tab"
          class="tabs__btn"
          :class="{ 'tabs__btn--on': view === 'journey' }"
          :aria-selected="view === 'journey'"
          @click="view = 'journey'"
        >
          Journey
        </button>
        <button
          type="button"
          role="tab"
          class="tabs__btn"
          :class="{ 'tabs__btn--on': view === 'badges' }"
          :aria-selected="view === 'badges'"
          @click="showBadges"
        >
          My Badges
        </button>
      </div>

      <!-- Trophy case: every award the fundraiser has earned, including a
           windowed badge earned more than once. -->
      <section v-if="view === 'badges'" class="case">
        <GivarooSpinner v-if="badgesLoading" class="mt-12" />

        <template v-else>
          <p v-if="!earnedBadges.length" class="case__empty">
            No badges yet. Collect your first recurring donation to get on the board.
          </p>

          <template v-else>
            <p class="case__summary">
              {{ earnedBadges.length }} {{ earnedBadges.length === 1 ? 'badge' : 'badges' }} earned
              · {{ earnedPoints }} points
            </p>

            <ul class="case__grid">
              <li v-for="badge in earnedBadges" :key="badge.id" class="trophy">
                <div
                  class="trophy__badge"
                  :class="{ 'trophy__badge--art': badge.template.image }"
                  :style="badge.template.image ? null : { background: trophyGradient(badge) }"
                >
                  <img
                    v-if="badge.template.image"
                    :src="badge.template.image"
                    class="trophy__art"
                    alt=""
                    aria-hidden="true"
                  />
                  <component
                    v-else
                    :is="resolveAchievementIcon(badge.template.icon)"
                    class="trophy__icon"
                    aria-hidden="true"
                  />
                </div>

                <p class="trophy__name">{{ badge.template.name }}</p>
                <p v-if="tierOf(badge)" class="trophy__tier">{{ tierOf(badge) }}</p>
                <p class="trophy__meta">{{ awardedLabel(badge) }}</p>
              </li>
            </ul>
          </template>

          <UsStatesMap />
        </template>
      </section>

      <!-- Horizontal roadmap: one coloured zone per badge type -->
      <div v-else class="road">
        <div class="zones">
          <section
            v-for="track in tracks"
            :key="track.name"
            class="zone"
            :class="{ 'zone--solo': track.nodes.length === 1 }"
            :style="{ background: track.theme.bg, borderColor: track.theme.border }"
          >
            <div class="zone__head">
              <span class="zone__icon" :style="{ background: track.theme.label }">
                <component
                  :is="resolveAchievementIcon(track.icon)"
                  class="h-3.5 w-3.5"
                  aria-hidden="true"
                />
              </span>
              <span class="zone__name" :style="{ color: track.theme.label }">{{ track.name }}</span>
              <span class="zone__count">{{ track.earnedCount }}/{{ track.nodes.length }}</span>
            </div>

            <div class="zone__path">
              <!-- Inner track so the line/fill span the full scrollable width
                   rather than just the visible slice of a scrolled path. -->
              <div class="zone__track">
                <div class="zone__line"></div>
                <div
                  class="zone__fill"
                  :style="{ width: track.fillPct + '%', background: track.theme.label }"
                ></div>

                <div
                  v-for="(node, i) in track.nodes"
                  :key="node.templateId"
                  class="stop"
                  :class="i % 2 ? 'stop--down' : 'stop--up'"
                >
                  <span
                    class="stop__dot"
                    :style="node.earned ? { background: track.theme.label } : null"
                  ></span>

                  <div
                    class="node"
                    :class="{
                      'node--earned': node.earned,
                      'node--locked': !node.earned,
                      'node--next': node.isNext,
                    }"
                    tabindex="0"
                    :aria-describedby="`req-${node.templateId}`"
                  >
                    <span v-if="node.isNext" class="node__next-tag">Next</span>

                    <div
                      class="node__badge"
                      :class="{ 'node__badge--art': node.image }"
                      :style="
                        node.earned && !node.image
                          ? { background: badgeGradient(node, track.theme) }
                          : null
                      "
                    >
                      <img
                        v-if="node.image"
                        :src="node.image"
                        class="node__art"
                        :class="{ 'node__icon--locked': !node.earned }"
                        alt=""
                        aria-hidden="true"
                      />
                      <component
                        v-else
                        :is="resolveAchievementIcon(node.icon)"
                        class="node__icon"
                        :class="{ 'node__icon--locked': !node.earned }"
                        aria-hidden="true"
                      />
                      <span v-if="!node.earned" class="node__lock">
                        <LockClosedIcon class="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span v-else class="node__check">
                        <CheckIcon class="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </div>

                    <p class="node__label">{{ track.isLadder ? node.tierDisplay : node.name }}</p>

                    <p v-if="node.earned" class="node__req node__req--earned">
                      {{ earnedLabel(node) }}
                    </p>

                    <span :id="`req-${node.templateId}`" class="node__tip" role="tooltip">
                      <span class="node__tip-label">
                        {{ node.earned ? 'Earned for' : 'How to earn' }}
                      </span>
                      {{ node.requirement }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { LockClosedIcon, CheckIcon } from '@heroicons/vue/24/solid'
import DashboardContainer from '@/components/DashboardContainer.vue'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import UsStatesMap from '@/components/UsStatesMap.vue'
import { achievementFunctions } from '@/services/achievements'
import { resolveAchievementIcon } from '@/components/achievementIcons'

// Soft background + accent per badge type, cycled by track order.
const ZONE_THEMES = [
  { bg: '#eef6f3', border: '#d7e8e1', label: '#2f7d6b' },
  { bg: '#f1eefb', border: '#e0d8f4', label: '#7a5cd0' },
  { bg: '#fdf4e3', border: '#f0e2c2', label: '#b57e14' },
  { bg: '#fdeef1', border: '#f4d9e0', label: '#a24a63' },
  { bg: '#eaf2fb', border: '#d5e4f4', label: '#2f6fb0' },
  { bg: '#eef7ee', border: '#d6ead6', label: '#3f8f52' },
  { bg: '#eef9fb', border: '#d3eaef', label: '#1f8a9c' },
]

export default {
  name: 'Achievements',
  components: {
    DashboardContainer,
    GivarooSpinner,
    UsStatesMap,
    LockClosedIcon,
    CheckIcon,
  },
  props: {
    pageTitle: {
      type: String,
      default: 'Achievements',
    },
  },
  setup() {
    const store = useStore()
    const { api } = achievementFunctions()
    const loading = ref(true)
    const catalog = ref([])

    const view = ref('journey')
    const earnedBadges = ref([])
    const badgesLoading = ref(false)
    let badgesLoaded = false

    // Group the (pre-sorted) catalog into per-type zones, each with its own
    // colour theme, progress fill, and nearest-locked ("next") badge.
    const tracks = computed(() => {
      const byTrack = new Map()
      for (const item of catalog.value) {
        if (!byTrack.has(item.track)) byTrack.set(item.track, [])
        byTrack.get(item.track).push(item)
      }
      let idx = 0
      return Array.from(byTrack, ([name, nodes]) => {
        // "Next" only after a track has been started. Untouched groups stay
        // quiet so every locked first badge isn't tagged at once.
        const earnedCount = nodes.filter((n) => n.earned).length
        const nextIdx = earnedCount > 0 ? nodes.findIndex((n) => !n.earned) : -1
        let lastEarned = -1
        nodes.forEach((n, i) => {
          if (n.earned) lastEarned = i
        })
        return {
          name,
          icon: nodes[0]?.icon,
          nodes: nodes.map((n, i) => ({ ...n, isNext: i === nextIdx })),
          isLadder: nodes.length > 1,
          earnedCount,
          fillPct: lastEarned < 0 ? 0 : Math.round(((lastEarned + 1) / nodes.length) * 100),
          theme: ZONE_THEMES[idx++ % ZONE_THEMES.length],
        }
      })
    })

    const totalCount = computed(() => catalog.value.length)
    const unlockedCount = computed(() => catalog.value.filter((n) => n.earned).length)
    // Backend User.points = badge XP + recurring-donation XP.
    const totalPoints = computed(() => store.getters.user?.points ?? 0)
    const progressPct = computed(() =>
      totalCount.value ? Math.round((unlockedCount.value / totalCount.value) * 100) : 0,
    )

    const gradient = (color) => `linear-gradient(135deg, ${color} 0%, ${shade(color, -20)} 100%)`

    // Earned badges are coloured by tier (bronze/silver/gold/diamond); tier-less
    // standalone badges fall back to their own configured colour.
    const TIER_COLORS = {
      bronze: '#C08457',
      silver: '#9AA6B2',
      gold: '#DBA520',
      diamond: '#57B6E6',
    }
    const badgeGradient = (node, theme) =>
      gradient(TIER_COLORS[node.tier] || node.bannerColor || theme?.label || '#B57E14')

    function shade(hex, percent) {
      const clean = (hex || '').replace('#', '')
      if (clean.length !== 6) return hex
      const num = parseInt(clean, 16)
      const amt = Math.round(2.55 * percent)
      const clamp = (v) => Math.max(0, Math.min(255, v))
      const r = clamp((num >> 16) + amt)
      const g = clamp(((num >> 8) & 0x00ff) + amt)
      const b = clamp((num & 0x0000ff) + amt)
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
    }

    // The catalog collapses each template to one row, so it cannot show a
    // windowed badge earned in several different weeks. The trophy case reads
    // the award rows instead, which is why it is a separate fetch.
    const showBadges = async () => {
      view.value = 'badges'
      if (badgesLoaded) return
      badgesLoading.value = true
      try {
        earnedBadges.value = await api.csc.listMine()
        badgesLoaded = true
      } catch (err) {
        console.error('Failed to load earned badges:', err)
      } finally {
        badgesLoading.value = false
      }
    }

    const earnedPoints = computed(() =>
      earnedBadges.value.reduce((sum, b) => sum + (b.pointsAwarded || 0), 0),
    )

    const tierOf = (badge) => {
      const tier = badge.template?.tierDisplay
      return tier && tier !== 'None' ? tier : ''
    }

    const trophyGradient = (badge) =>
      gradient(TIER_COLORS[badge.tier] || badge.template?.bannerColor || '#B57E14')

    const awardedLabel = (badge) => {
      const date = badge.datetimeCreated
        ? new Date(badge.datetimeCreated).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : ''
      const pts = badge.pointsAwarded ? `+${badge.pointsAwarded} pts` : ''
      return [date, pts].filter(Boolean).join(' · ')
    }

    const earnedLabel = (node) => {
      const pts = node.pointsAwarded ? `+${node.pointsAwarded} pts` : ''
      const date = node.earnedAt
        ? new Date(node.earnedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : ''
      return [pts, date].filter(Boolean).join(' · ') || 'Unlocked'
    }

    onMounted(async () => {
      try {
        await store.dispatch('fetchUser')
        catalog.value = await api.csc.getCatalog()
      } catch (err) {
        console.error('Failed to load achievement catalog:', err)
      } finally {
        loading.value = false
      }
    })

    return {
      loading,
      tracks,
      view,
      showBadges,
      earnedBadges,
      badgesLoading,
      earnedPoints,
      tierOf,
      trophyGradient,
      awardedLabel,
      totalCount,
      unlockedCount,
      totalPoints,
      progressPct,
      gradient,
      badgeGradient,
      earnedLabel,
      resolveAchievementIcon,
    }
  },
}
</script>

<style scoped>
/* ---- Hero ---- */
.journey-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1.15rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
  color: #fff;
  background: linear-gradient(135deg, #2f7d6b 0%, #1f5e6e 55%, #7a5cd0 130%);
  box-shadow: 0 8px 20px rgba(31, 94, 110, 0.22);
}
.journey-hero__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}
.journey-hero__sub {
  margin: 0.15rem 0 0;
  font-size: 0.8125rem;
  opacity: 0.9;
}
.journey-hero__progress {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 12rem;
  flex: 1;
  max-width: 20rem;
}
.journey-hero__bar {
  flex: 1;
  height: 0.5rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
  overflow: hidden;
}
.journey-hero__fill {
  height: 100%;
  border-radius: 999px;
  background: #fdd835;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}
.journey-hero__pct {
  font-size: 0.875rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

/* ---- Journey / My Badges switcher ---- */
.tabs {
  display: flex;
  gap: 0.375rem;
  margin: 0 0 0.875rem;
}
.tabs__btn {
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  transition: background 0.15s ease, color 0.15s ease;
}
.tabs__btn:hover {
  color: #374151;
}
.tabs__btn--on {
  color: #fff;
  background: #b57e14;
}

/* ---- Trophy case ---- */
.case__summary {
  margin: 0 0 0.875rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #4b5563;
}
.case__empty {
  margin: 1.5rem 0;
  font-size: 0.875rem;
  color: #9ca3af;
  text-align: center;
}
.case__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  gap: 0.75rem;
}
.trophy {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.875rem 0.625rem;
  border: 1px solid #eceef1;
  border-radius: 0.85rem;
  background: #fff;
  text-align: center;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}
.trophy__badge {
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.5rem;
  border-radius: 0.75rem;
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.14);
}
.trophy__badge--art {
  background: transparent;
  box-shadow: none;
}
.trophy__icon {
  width: 1.6rem;
  height: 1.6rem;
}
.trophy__art {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
}
.trophy__name {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2937;
  overflow-wrap: anywhere;
}
.trophy__tier {
  margin: 0.15rem 0 0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b57e14;
}
.trophy__meta {
  margin: 0.25rem 0 0;
  font-size: 0.6875rem;
  color: #9ca3af;
}

/* ---- Horizontal roadmap ---- */
.road {
  margin-top: 0;
  padding-bottom: 0.25rem;
}
.zones {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.875rem;
  padding: 0;
}

/* ---- One coloured zone per badge type ----
   Zones flex to share each row rather than sizing to their content, so the
   board reflows cleanly from a phone (one per row) up to a wide desktop. */
.zone {
  position: relative;
  flex: 1 1 22rem;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid;
  border-radius: 1rem;
  padding: 0.7rem 0.85rem 0.8rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03);
}
/* In normal flow (not absolute) so the zone widens to fit its title instead of
   clipping it, and so the path needs no top padding to clear the header. */
.zone__head {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.15rem;
}
.zone__icon {
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;
  flex: none;
  border-radius: 0.4rem;
  color: #fff;
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.18);
}
.zone__name {
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}
.zone__count {
  flex: none;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #98a0a8;
  font-variant-numeric: tabular-nums;
}

/* Stops share the width down to a floor, then the track scrolls, so a type can
   gain tiers without the cards being squeezed to nothing.
   A scroll container clips its other axis too, so everything that hangs outside
   a card (the "Next" tag, the tooltip) has to stay within this padding - and
   the vertical padding must stay symmetric or the 50% line drifts off the dots. */
.zone__path {
  flex: 1;
  display: flex;
  align-items: stretch;
  min-height: 8.5rem;
  padding: 0.75rem 0.5rem;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
}
/* Fills the path, then grows past it once the stops stop fitting. */
.zone__track {
  position: relative;
  flex: 1 0 auto;
  min-width: 100%;
  display: flex;
  align-items: stretch;
  gap: 0.3rem;
}
.zone__path::-webkit-scrollbar {
  height: 5px;
}
.zone__path::-webkit-scrollbar-track {
  background: transparent;
}
.zone__path::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(31, 41, 55, 0.18);
}

/* Single-badge zones: one centred, featured badge (no progress line needed).
   Wider stop so standalone names have room to breathe. */
.zone--solo {
  flex: 1 1 11rem;
  max-width: 20rem;
}
.zone--solo .zone__line,
.zone--solo .zone__fill,
.zone--solo .stop__dot {
  display: none;
}
.zone--solo .zone__track {
  justify-content: center;
}
.zone--solo .stop {
  flex: 0 1 10rem;
  grid-template-rows: 1fr;
}
.zone--solo .node {
  max-width: 10rem;
}
/* Needs to outrank .stop--up/.stop--down, which otherwise pin the lone badge to
   an edge of the zone instead of centring it. */
.zone--solo .stop .node {
  grid-row: 1;
  align-self: center;
}

.zone__line {
  position: absolute;
  left: 0.6rem;
  right: 0.6rem;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  background: rgba(31, 41, 55, 0.1);
  border-radius: 999px;
}
.zone__fill {
  position: absolute;
  left: 0.6rem;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 1;
}

/* ---- Stops flanking the zone's line ---- */
.stop {
  position: relative;
  z-index: 2;
  /* Shares spare width, but never shrinks past a readable card. */
  flex: 1 1 5.5rem;
  min-width: 5.5rem;
  display: grid;
  grid-template-rows: 1fr auto 1fr;
  align-items: center;
  justify-items: center;
  row-gap: 0.6rem;
}
.stop__dot {
  grid-row: 2;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: #c7cfd2;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.85);
  z-index: 3;
}

.node {
  /* Positioning context for the "Next" tag and the hover tooltip; without it
     both anchor to .stop and drift away from the card. */
  position: relative;
  width: 100%;
  max-width: 7rem;
  min-height: 4.9rem;
  cursor: help;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.5rem 0.4rem 0.55rem;
  background: #fff;
  border: 1px solid #ecebe6;
  border-radius: 0.8rem;
  box-shadow: 0 3px 10px rgba(16, 24, 40, 0.08);
}
.stop--up .node {
  grid-row: 1;
  align-self: end;
}
.stop--down .node {
  grid-row: 3;
  align-self: start;
}
.node--earned {
  animation: node-pop 0.4s cubic-bezier(0.22, 1.3, 0.36, 1) both;
}
.node--next {
  border-color: transparent;
  box-shadow: 0 0 0 2px #fdd835, 0 6px 16px rgba(0, 0, 0, 0.12);
  animation: next-pulse 1.8s ease-in-out infinite;
}
.node:focus-visible {
  outline: 2px solid #2f7d6b;
  outline-offset: 2px;
}
.node__next-tag {
  position: absolute;
  top: -0.55rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  background: #fdd835;
  color: #4a3a00;
  font-size: 0.5625rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  z-index: 4;
}

/* Cards stay uncluttered: the criteria is revealed on hover/focus rather than
   taking up permanent space. Matches the card's width and grows toward the
   middle of the track - the one direction with guaranteed room, so the track's
   horizontal scrolling never clips it. */
.node__tip {
  position: absolute;
  left: 0;
  right: 0;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.45rem;
  border-radius: 0.8rem;
  background: rgba(17, 24, 39, 0.96);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  overflow-wrap: anywhere;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
  z-index: 5;
}
.stop--up .node__tip {
  top: 0;
}
.stop--down .node__tip {
  bottom: 0;
}
.node__tip-label {
  display: block;
  margin-bottom: 0.15rem;
  font-size: 0.5rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  opacity: 0.65;
}
.node:hover .node__tip,
.node:focus .node__tip {
  opacity: 1;
}

.node__badge {
  position: relative;
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  margin-bottom: 0.35rem;
  border-radius: 0.6rem;
  background: #eef0f2;
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.12);
}
.node__icon {
  width: 1.1rem;
  height: 1.1rem;
}
.node__icon--locked {
  color: #b6bcc4;
  filter: grayscale(1) blur(2.5px);
  opacity: 0.8;
}
/* Uploaded artwork replaces the tinted tile, so the tile itself gets out of
   the way rather than boxing the image in a coloured square. */
.node__badge--art {
  background: transparent;
  box-shadow: none;
}
.node__art {
  width: 2.1rem;
  height: 2.1rem;
  object-fit: contain;
}
.node__lock {
  position: absolute;
  display: grid;
  place-items: center;
  color: #6b7280;
}
.node__check {
  position: absolute;
  right: -0.25rem;
  bottom: -0.25rem;
  display: grid;
  place-items: center;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  background: #2f7d6b;
  color: #fff;
  box-shadow: 0 0 0 2px #fff;
}

.node__label {
  margin: 0;
  max-width: 100%;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
.node--locked .node__label {
  color: #9ca3af;
}
.node__req {
  margin: 0.15rem 0 0;
  max-width: 100%;
  font-size: 0.625rem;
  line-height: 1.25;
  color: #9ca3af;
  overflow-wrap: anywhere;
}
.node__req--earned {
  color: #1f2937;
  font-weight: 700;
}

@keyframes node-pop {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
@keyframes next-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 2px #fdd835, 0 6px 16px rgba(0, 0, 0, 0.12);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(253, 216, 53, 0.35), 0 6px 16px rgba(0, 0, 0, 0.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .node--earned,
  .node--next {
    animation: none;
  }
  .journey-hero__fill,
  .zone__fill,
  .node__tip {
    transition: none;
  }
}
</style>
